import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

type QMenuSubLevelContextValue = {
  /** Id of the open QMenuSub at this menu level, or null. */
  activeId: string | null;
  setActiveId: (id: string | null) => void;
  /**
   * Close only if `id` is still the active sub. Pending hover-close timers on a
   * previous sibling must not clear a peer that already took over.
   */
  clearActiveIdIf: (id: string) => void;
};

const QMenuSubLevelContext = createContext<QMenuSubLevelContextValue | null>(null);

/**
 * Sibling exclusivity for {@link QMenuSub}: opening one sub at a level closes peers.
 * Provided by {@link QMenu} for the root panel and by each open QMenuSub for its nested panel.
 */
export function QMenuSubLevelProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [activeId, setActiveIdState] = useState<string | null>(null);
  const setActiveId = useCallback((id: string | null) => {
    setActiveIdState(id);
  }, []);
  const clearActiveIdIf = useCallback((id: string) => {
    setActiveIdState((current) => (current === id ? null : current));
  }, []);
  const value = useMemo(() => ({ activeId, setActiveId, clearActiveIdIf }), [activeId, setActiveId, clearActiveIdIf]);
  return <QMenuSubLevelContext.Provider value={value}>{children}</QMenuSubLevelContext.Provider>;
}

export function useQMenuSubLevel(): QMenuSubLevelContextValue | null {
  return useContext(QMenuSubLevelContext);
}
